import React, { useState, useEffect } from 'react';
import { getCurrentUser, getUserType } from '../services/authService';
import api from '../services/api';
import DocumentosModal from './DocumentosModal';
import AnalisisPdfModal from './AnalisisPdfModal';
import { ExclamationTriangleIcon, DocumentIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Badge, Card, IconButton, LoadingState, EmptyState } from './ui';
import { formatFecha } from '../utils/fecha';

interface ResultadoBusqueda {
  id: number;
  numAnalisis: string;
  origen: string;
  destino: string;
  fecha: string;
  tienePdf: boolean;
  estado: number;
}

interface ExtendedUser {
  id: number;
  username?: string;
  nombre?: string;
  apellido?: string;
  email?: string;
  tipo?: string;
  tabla_grupo?: string;
  id_grupo?: number;
  info_adicional?: any;
}

const MisAnalisis: React.FC = () => {
  const [resultados, setResultados] = useState<ResultadoBusqueda[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [userType, setUserType] = useState<string>('');
  const [userInfo, setUserInfo] = useState<any>(null);
  const [documentosModalOpen, setDocumentosModalOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedAnalisisId, setSelectedAnalisisId] = useState<number | null>(null);
  const [selectedPdfAnalisisId, setSelectedPdfAnalisisId] = useState<number | null>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await getCurrentUser();
        const type = getUserType() || '';
        
        console.log('Usuario actual:', user);
        console.log('Tipo de usuario:', type);
        
        setCurrentUser(user);
        setUserType(type);
        
        if ((type === 'cliente' || type === 'laboratorio') && user && user.id_grupo) {
          try {
            let userInfoResponse;
            if (type === 'cliente') {
              userInfoResponse = await api.get(`/clientes/${user.id_grupo}`);
              console.log('Información del cliente obtenida:', userInfoResponse.data);
            } else if (type === 'laboratorio') {
              userInfoResponse = await api.get(`/laboratorios/${user.id_grupo}`);
              console.log('Información del laboratorio obtenida:', userInfoResponse.data);
            }
            
            setUserInfo(userInfoResponse?.data);
            cargarMisAnalisis(user.id_grupo, type);
          } catch (err) {
            console.error(`Error al obtener información del ${type}:`, err);
            setErrorMessage(`Error al obtener información del ${type}`);
          }
        } else {
          setErrorMessage('Esta función solo está disponible para clientes y laboratorios');
        }
      } catch (error) {
        console.error('Error general al obtener información del usuario:', error);
        setErrorMessage('Error al obtener información del usuario');
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []); // Eliminadas las dependencias que causaban el bucle infinito

  const cargarMisAnalisis = async (grupoId: number, tipoUsuario: string) => {
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setErrorMessage('No hay una sesión activa. Por favor, inicie sesión nuevamente.');
        return;
      }
      
      console.log(`Cargando análisis para el ${tipoUsuario} ID:`, grupoId);
      
      // Para clientes buscar por id_origen, para laboratorios por id_lab
      const searchParams = tipoUsuario === 'cliente' 
        ? { id_origen: grupoId }
        : { id_lab: grupoId };
      
      const response = await api.get('/analisis/search', { 
        params: searchParams,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Respuesta de análisis recibida:', response.status, response.data);
      setResultados(response.data);
      
      if (response.data.length === 0) {
        const entidad = tipoUsuario === 'cliente' ? 'empresa' : 'laboratorio';
        setErrorMessage(`No se encontraron análisis para su ${entidad}`);
      }
    } catch (error: any) {
      console.error('Error al cargar mis análisis:', error);
      
      if (error.response) {
        if (error.response.status === 401) {
          setErrorMessage('La sesión ha caducado. Por favor, inicie sesión nuevamente.');
        } else if (error.response.status === 403) {
          setErrorMessage('No tiene permisos para ver esta información.');
        } else {
          setErrorMessage(`Error al cargar los datos: ${error.response.data?.message || 'Error del servidor'}`);
        }
      } else if (error.request) {
        setErrorMessage('No se pudo conectar con el servidor. Verifique su conexión a internet.');
      } else {
        setErrorMessage(`Error al cargar los datos: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const abrirDocumentos = (analisisId: number) => {
    setSelectedAnalisisId(analisisId);
    setDocumentosModalOpen(true);
  };

  const abrirPdf = (analisisId: number) => {
    setSelectedPdfAnalisisId(analisisId);
    setPdfModalOpen(true);
  };

  const getEstadoTono = (estado: number): 'warning' | 'success' | 'neutral' => {
    switch (estado) {
      case 0: return 'warning';
      case 1: return 'success';
      default: return 'neutral';
    }
  };

  const getEstadoTexto = (estado: number) => {
    switch (estado) {
      case 0: return 'Pendiente';
      case 1: return 'Completado';
      default: return 'Desconocido';
    }
  };

  if (userType !== 'cliente' && userType !== 'laboratorio') {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-xl font-bold text-red-800 mb-2">Acceso denegado</h2>
          <p className="text-red-700">Esta función solo está disponible para clientes y laboratorios.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <Card padded={false}>
        <div className="p-4 bg-brand-50 border-b border-brand-100">
          <h2 className="font-bold text-xl text-brand-800">Análisis</h2>
          {userInfo && (
            <p className="text-sm text-brand-600 mt-1">
              {userType === 'cliente' ? 'Análisis de' : 'Análisis asignados a'}: {userInfo.nombre || userInfo.razon_social}
            </p>
          )}
        </div>

        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        {(isLoading || loadingUserInfo) ? (
          <LoadingState label="Cargando análisis..." />
        ) : resultados.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Número de análisis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {userType === 'cliente' ? 'Laboratorio' : 'Cliente'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {resultados.map((resultado) => (
                  <tr key={resultado.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {resultado.numAnalisis}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatFecha(resultado.fecha)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {userType === 'cliente' ? resultado.destino : resultado.origen}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge tone={getEstadoTono(resultado.estado)}>
                        {getEstadoTexto(resultado.estado)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-1">
                        <IconButton
                          onClick={() => abrirDocumentos(resultado.id)}
                          label="Documentos"
                          tone="brand"
                          icon={<DocumentIcon className="w-5 h-5" />}
                        />

                        {resultado.tienePdf && (
                          <IconButton
                            onClick={() => abrirPdf(resultado.id)}
                            label="Ver PDF"
                            tone="brand"
                            icon={<DocumentTextIcon className="w-5 h-5" />}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !errorMessage ? (
          <EmptyState title="No se encontraron análisis." />
        ) : null}
      </Card>

      {documentosModalOpen && selectedAnalisisId && (
        <DocumentosModal
          isOpen={documentosModalOpen}
          onClose={() => {
            setDocumentosModalOpen(false);
            setSelectedAnalisisId(null);
          }}
          analisisId={selectedAnalisisId}
        />
      )}

      {pdfModalOpen && selectedPdfAnalisisId && (
        <AnalisisPdfModal
          isOpen={pdfModalOpen}
          onClose={() => {
            setPdfModalOpen(false);
            setSelectedPdfAnalisisId(null);
          }}
          analisisId={selectedPdfAnalisisId}
        />
      )}
    </div>
  );
};

export default MisAnalisis;
